package nl.volleyplanner.web.base.routing

import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.launch
import nl.volleyplanner.web.CoroutineManager
import nl.volleyplanner.web.Logger
import nl.volleyplanner.web.base.controller.NotFoundPageController
import org.w3c.dom.HTMLElement

data class PageRouteContext(
    val urlState: UrlState,
    val routePattern: String,
    val pathParameters: Map<String, String>
) {
    companion object {
        fun getChangeTypes(pageRouteContextA: PageRouteContext?, pageRouteContextB: PageRouteContext?) = buildSet {
            if (pageRouteContextA?.urlState?.path != pageRouteContextB?.urlState?.path) add(RouteChangeType.PageChanged)
            if (pageRouteContextA?.urlState?.queryParameters != pageRouteContextB?.urlState?.queryParameters) add(RouteChangeType.QueryChanged)
            if (pageRouteContextA?.urlState?.hash != pageRouteContextB?.urlState?.hash) add(RouteChangeType.HashChanged)
        }
    }
}

data class PageMountContext(
    val pageRouteContext: PageRouteContext,
    val root: HTMLElement
)

enum class RouteChangeType {
    PageChanged,
    QueryChanged,
    HashChanged
}

data class RouteChange(
    val previous: PageRouteContext,
    val current: PageRouteContext,
    val changeTypes: Set<RouteChangeType>,
    val navigationCause: NavigationCause
)

interface RouteHandler {
    suspend fun mount(context: PageMountContext)
    suspend fun update(change: RouteChange)
    suspend fun unmount()
}

interface PageRouteController : RouteHandler {
    /**
     * Temporary function to ensure a new instance is created when loading a controller
     * As this enables better coroutine management
     *
     * TODO: In the future need to extract route information from controllers
     *  so controller instances only need to be created when actually needing them
     */
    fun createRuntimeInstance() : PageRouteController
}

sealed interface RouteGuardResult {
    object Allow : RouteGuardResult
    data class Deny(
        val createController: () -> PageRouteController
    ) : RouteGuardResult
}

interface RouteGuard {
    suspend fun canActivate(context: PageRouteContext): RouteGuardResult
}

data class RouteDefinition(
    val pattern: String,
    val createHandler: () -> PageRouteController,
    val routeGuards: List<RouteGuard> = emptyList()
)

object PageRouter {
    private val logger = Logger.getLogger("nl.volleyplanner.web.base.routing.PageRouter")

    private val routes = mutableListOf<RouteDefinition>()

    private var pageRoot: HTMLElement? = null
    private var routingJob: Job? = null

    private var currentCreateHandler: (() -> PageRouteController)? = null
    private var currentRoute: RouteDefinition? = null
    private val currentContext: MutableStateFlow<PageRouteContext?> = MutableStateFlow(null)
    val currentPageRouteContext = currentContext.asStateFlow()
    private var currentController: PageRouteController? = null

    fun initialize(scope: CoroutineScope, pageRoot: HTMLElement) {
        logger.debug { "Initializing page router" }
        NavigationManager.initialize()
        if (routingJob != null) return
        this.pageRoot = pageRoot

        logger.debug { "Launching routing job" }
        routingJob = scope.launch {
            logger.debug { "Trying first URL Snapshot" }
            //handleUrlSnapshot(NavigationManager.state.value)

            NavigationManager.state.collectLatest { urlSnapshot ->
                try {
                    handleUrlSnapshot(urlSnapshot)
                } catch (e: CancellationException) {
                    logger.warn { "Routing job cancelled: $e" }
                    throw e
                } catch (t: Throwable) {
                    logger.warn { "Error handling URL snapshot: $urlSnapshot: ${t.message}" }
                    console.error(t.printStackTrace())
                }
            }
        }
    }

    suspend fun dispose() {
        logger.debug { "Disposing page router" }
        routingJob?.cancel()
        routingJob = null

        currentController?.unmount()
        currentController = null
        currentContext.value = null
        currentRoute = null
    }

    fun register(
        pattern: String,
        guards: List<RouteGuard> = emptyList(),
        createHandler: () -> PageRouteController
    ) {
        logger.debug { "Registering route '$pattern'" }
        routes += RouteDefinition(pattern, createHandler, guards)
    }

    suspend fun forceReload() {
        handleUrlSnapshot(NavigationManager.state.value, true)
    }

    private suspend fun handleUrlSnapshot(urlSnapshot: UrlSnapshot, forceReload: Boolean = false) {
        logger.info { "Handling URL snapshot: $urlSnapshot" }
        val nextMatch = findMatch(urlSnapshot.state)
        if (nextMatch == null) {
            val pageRouteContext = PageRouteContext(urlSnapshot.state, "", emptyMap())
            currentController?.unmount()
            currentRoute = null
            currentContext.value = pageRouteContext
            currentController = NotFoundPageController()
            logger.debug { "Mounting controller: ${currentController!!::class.simpleName} (${urlSnapshot})" }
            currentController!!.mount(PageMountContext(pageRouteContext, pageRoot!!))
            return
        }

        val previousContext = currentContext.value
        val nextContext = nextMatch.context
        val changeTypes = PageRouteContext.getChangeTypes(previousContext, nextContext)

        val nextCreateHandler = nextMatch.route.routeGuards.fold(nextMatch.route.createHandler, { handler, guard ->
            val result = guard.canActivate(nextContext)
            if (result is RouteGuardResult.Allow) handler
            else if (result is RouteGuardResult.Deny) result.createController
            else throw IllegalStateException("Unknown route guard result: $result")
        })

        if (currentController == null || nextMatch.route != currentRoute || currentCreateHandler != nextCreateHandler) {
            CoroutineManager.newControllerScope()
            currentController?.unmount()
            // TODO: Need to get a new way of instantiating controllers
            //  Need a new instance to create a new coroutine scope
            //  But (legacy) controllers are already instantiated and won't have an active coroutine scope otherwise
            val nextController = nextCreateHandler.invoke()
                .createRuntimeInstance()

            currentCreateHandler = nextCreateHandler
            currentRoute = nextMatch.route
            currentContext.value = nextContext
            currentController = nextController

            logger.info { "Mounting controller: ${nextController::class.simpleName} for pattern ${currentRoute!!.pattern} (${urlSnapshot})" }
            nextController.mount(PageMountContext(nextContext, pageRoot!!))
        } else {
            currentContext.value = nextContext
            logger.debug { "Updating controller: ${currentController?.let { it::class.simpleName }} (${urlSnapshot})" }
            currentController?.update(RouteChange(previousContext!!, nextContext, changeTypes, urlSnapshot.cause))
        }
    }

    private data class PageRouteMatch(
        val route: RouteDefinition,
        val context: PageRouteContext
    )

    private fun findMatch(urlState: UrlState) : PageRouteMatch? {
        routes.forEach { route ->
            val pathParameters = matchPath(route.pattern, urlState.path)
            if (pathParameters != null) {
                return PageRouteMatch(route, PageRouteContext(
                    urlState = urlState,
                    routePattern = route.pattern,
                    pathParameters = pathParameters
                ))
            }
        }
        return null
    }

    private fun matchPath(pattern: String, path: String) : Map<String, String>? {
        val patternParts = splitPath(pattern)
        val pathParts = splitPath(path)
        if (patternParts.size != pathParts.size) return null

        val pathParameters = mutableMapOf<String, String>()
        patternParts.zip(pathParts).forEach { (patternPart, pathPart) ->
            if (patternPart.startsWith(":")) {
                val parameterName = patternPart.removePrefix(":")
                if (parameterName.isBlank()) return null

                pathParameters[parameterName] = pathPart
            } else if (patternPart != pathPart) {
                return null
            }
        }
        return pathParameters
    }

    private fun splitPath(path: String): List<String> {
        return path.trim('/').split('/').filter { part -> part.isNotBlank() }
    }
}